import { useSubscription } from '@apollo/client';
import {
  VENDOR_OPPORTUNITY_UPDATED,
  NOTIFICATION_RECEIVED,
  CREDIT_SUBMISSION_UPDATED,
  DEAL_ACTIVITY_UPDATED,
  AI_DECISION_PARSING_COMPLETED,
  SMS_ACTIVITY_RECEIVED,
} from '../queries/subscriptions';

/**
 * Subscribe to real-time updates on a Vendor Opportunity.
 *
 * Usage:
 *   const { data, loading, error } = useVendorOpportunityUpdated(voId);
 */
export function useVendorOpportunityUpdated(vendorOpportunityId: string) {
  return useSubscription(VENDOR_OPPORTUNITY_UPDATED, {
    variables: { vendorOpportunityId },
    skip: !vendorOpportunityId,
  });
}

/**
 * Subscribe to real-time notifications for the current user.
 *
 * Usage:
 *   const { data } = useNotificationReceived(userProfile.id);
 */
export function useNotificationReceived(userProfileId: string) {
  return useSubscription(NOTIFICATION_RECEIVED, {
    variables: { userProfileId },
    skip: !userProfileId,
  });
}

/**
 * Subscribe to credit submission updates for a Vendor Opportunity.
 *
 * Usage:
 *   const { data } = useCreditSubmissionUpdated(voId);
 */
export function useCreditSubmissionUpdated(vendorOpportunityId: string) {
  return useSubscription(CREDIT_SUBMISSION_UPDATED, {
    variables: { vendorOpportunityId },
    skip: !vendorOpportunityId,
  });
}

/**
 * Subscribe to deal activity updates for a Vendor Opportunity.
 *
 * Usage:
 *   const { data } = useDealActivityUpdated(voId);
 */
export function useDealActivityUpdated(vendorOpportunityId: string) {
  return useSubscription(DEAL_ACTIVITY_UPDATED, {
    variables: { vendorOpportunityId },
    skip: !vendorOpportunityId,
  });
}

/**
 * Subscribe to AI credit-decision parsing completion for a credit submission.
 *
 * Usage:
 *   const { data } = useAiDecisionParsingCompleted(creditSubmissionId);
 *   // data?.aiDecisionParsingCompleted.success
 */
export function useAiDecisionParsingCompleted(creditSubmissionId: string | null) {
  return useSubscription(AI_DECISION_PARSING_COMPLETED, {
    variables: { creditSubmissionId },
    skip: !creditSubmissionId,
  });
}

export function useSmsActivityReceived(vendorOpportunityId: string | null) {
  return useSubscription(SMS_ACTIVITY_RECEIVED, {
    variables: { vendorOpportunityId },
    skip: !vendorOpportunityId,
  });
}
